import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Heart, Bookmark, Share2, MessageCircle, Flag, Send, ChevronLeft, ChevronRight,
  Plus, Search, MapPin, Filter, FileText, Paperclip, X, User, Phone, Mail, Loader2, Trash2,
  ArrowLeft, Home, LayoutDashboard
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public', desc: 'Visible to everyone' },
  { value: 'agents_only', label: 'Agents Only', desc: 'Visible after login' },
  { value: 'premium_agents', label: 'Premium Agents', desc: 'Premium only' },
];

const AlbriksConnect = () => {
  const { token, agent } = useAuth();
  const navigate = useNavigate();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const isLoggedIn = !!token && !!agent;
  const dashboardPath = agent?.role === 'broker' ? '/broker' : '/dashboard';

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [showContact, setShowContact] = useState(null);
  const [contactInfo, setContactInfo] = useState(null);
  const [showReport, setShowReport] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [expandedComments, setExpandedComments] = useState({});
  const [commentTexts, setCommentTexts] = useState({});
  const [commentNames, setCommentNames] = useState({});
  const [postComments, setPostComments] = useState({});

  const visibilityOptions = VISIBILITY_OPTIONS.filter(o =>
    o.value !== 'premium_agents' || agent?.is_premium
  );
  const [form, setForm] = useState({ title: '', category: '', city: '', zip_code: '', description: '', visibility: 'public', attachment_url: '', attachment_name: '' });
  const [creating, setCreating] = useState(false);
  const [editingPostId, setEditingPostId] = useState(null);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (filterCategory) params.append('category', filterCategory);
      if (filterCity) {
        if (/^\d{5}$/.test(filterCity.trim())) {
          params.append('zip_code', filterCity.trim());
        } else {
          params.append('city', filterCity.trim());
        }
      }

      let res;
      if (isLoggedIn) {
        res = await axios.get(`${API}/connect/posts?${params}`, { headers });
      } else {
        res = await axios.get(`${API}/connect/feed?${params}`);
      }
      setPosts(res.data.posts || []);
      setTotalPages(res.data.total_pages || 1);
    } catch { toast.error('Failed to load posts'); }
    finally { setLoading(false); }
  }, [page, filterCategory, filterCity, isLoggedIn]);

  useEffect(() => {
    axios.get(`${API}/connect/categories`).then(r => setCategories(r.data || [])).catch(() => {});
    fetchPosts();
  }, [fetchPosts]);

  useEffect(() => { setPage(1); }, [filterCategory, filterCity]);

  const handleCreate = async () => {
    if (!form.title.trim() || !form.category || !form.description.trim()) {
      toast.error('Title, category and description are required'); return;
    }
    setCreating(true);
    try {
      if (editingPostId) {
        await axios.put(`${API}/connect/posts/${editingPostId}`, form, { headers });
        toast.success('Post updated!');
      } else {
        await axios.post(`${API}/connect/posts`, form, { headers });
        toast.success('Post created!');
      }
      setShowCreate(false);
      setEditingPostId(null);
      setForm({ title: '', category: '', city: '', zip_code: '', description: '', visibility: 'public', attachment_url: '', attachment_name: '' });
      fetchPosts();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save post'); }
    finally { setCreating(false); }
  };

  const openEditPost = (post) => {
    setForm({
      title: post.title || '',
      category: post.category || '',
      city: post.city || '',
      zip_code: post.zip_code || '',
      description: post.description || '',
      visibility: post.visibility || 'public',
      attachment_url: post.attachment_url || '',
      attachment_name: post.attachment_name || '',
    });
    setEditingPostId(post.id);
    setShowCreate(true);
  };

  const toggleLike = async (postId) => {
    if (!isLoggedIn) { toast.error('Please log in to like posts'); return; }
    try {
      const res = await axios.post(`${API}/connect/posts/${postId}/like`, {}, { headers });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_liked: res.data.liked, likes_count: p.likes_count + (res.data.liked ? 1 : -1) } : p));
    } catch { toast.error('Failed'); }
  };

  const toggleSave = async (postId) => {
    if (!isLoggedIn) { toast.error('Please log in to save posts'); return; }
    try {
      const res = await axios.post(`${API}/connect/posts/${postId}/save`, {}, { headers });
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_saved: res.data.saved, saves_count: p.saves_count + (res.data.saved ? 1 : -1) } : p));
    } catch { toast.error('Failed'); }
  };

  const handleShare = (post) => {
    const url = `${window.location.origin}/connect?post=${post.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const handleContact = async (postId) => {
    try {
      const res = await axios.get(`${API}/connect/posts/${postId}/contact`);
      setContactInfo(res.data);
      setShowContact(postId);
    } catch { toast.error('Failed to get contact info'); }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) { toast.error('Please provide a reason'); return; }
    try {
      await axios.post(`${API}/connect/posts/${showReport}/report`, { reason: reportReason }, { headers });
      toast.success('Post reported');
      setShowReport(null);
      setReportReason('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to report'); }
  };

  const toggleComments = async (postId) => {
    if (expandedComments[postId]) {
      setExpandedComments(prev => ({ ...prev, [postId]: false }));
      return;
    }
    try {
      const res = await axios.get(`${API}/connect/posts/${postId}`);
      setPostComments(prev => ({ ...prev, [postId]: res.data.comments || [] }));
      setExpandedComments(prev => ({ ...prev, [postId]: true }));
    } catch { toast.error('Failed to load comments'); }
  };

  const submitComment = async (postId) => {
    const text = commentTexts[postId]?.trim();
    if (!text) return;
    try {
      let res;
      if (isLoggedIn) {
        res = await axios.post(`${API}/connect/posts/${postId}/comments`, { text }, { headers });
      } else {
        const name = commentNames[postId]?.trim() || '';
        res = await axios.post(`${API}/connect/posts/${postId}/comments/public`, { text, name });
      }
      setPostComments(prev => ({ ...prev, [postId]: [res.data, ...(prev[postId] || [])] }));
      setCommentTexts(prev => ({ ...prev, [postId]: '' }));
      setCommentNames(prev => ({ ...prev, [postId]: '' }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: p.comments_count + 1 } : p));
    } catch { toast.error('Failed to comment'); }
  };

  const deleteComment = async (postId, commentId) => {
    try {
      await axios.delete(`${API}/connect/comments/${commentId}`, { headers });
      setPostComments(prev => ({ ...prev, [postId]: (prev[postId] || []).filter(c => c.id !== commentId) }));
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, comments_count: Math.max(0, p.comments_count - 1) } : p));
    } catch { toast.error('Failed'); }
  };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const diff = (now - date) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const visLabel = (v) => VISIBILITY_OPTIONS.find(o => o.value === v)?.label || v;

  const Wrapper = isLoggedIn ? Layout : React.Fragment;

  return (
    <Wrapper>
      <div className={`max-w-3xl mx-auto ${!isLoggedIn ? 'px-4 py-8' : ''}`}>
        {/* Back Navigation */}
        {!isLoggedIn && (
          <div className="flex items-center gap-2 mb-4">
            <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="text-stone-600 hover:text-[#1A4D2E]">
              <ArrowLeft size={18} className="mr-2" /> Back to Home
            </Button>
          </div>
        )}
        {isLoggedIn && (
          <div className="flex items-center gap-2 mb-4">
            <Button onClick={() => navigate('/')} variant="ghost" size="sm" className="text-stone-600 hover:text-[#1A4D2E]">
              <Home size={16} className="mr-1.5" /> Home
            </Button>
            <Button onClick={() => navigate(dashboardPath)} variant="outline" size="sm" className="border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white">
              <LayoutDashboard size={16} className="mr-1.5" /> Dashboard
            </Button>
          </div>
        )}

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A4D2E]" style={{ fontFamily: '"Playfair Display", serif' }}>Albriks Connect</h1>
            <p className="text-stone-500 text-sm mt-1">Community forum for real estate professionals</p>
          </div>
          {isLoggedIn && (
            <Button onClick={() => setShowCreate(true)} className="bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full" data-testid="create-post-btn">
              <Plus size={16} className="mr-1.5" /> New Post
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex-1 min-w-[180px]">
            <Select value={filterCategory || '__all__'} onValueChange={v => setFilterCategory(v === '__all__' ? '' : v)}>
              <SelectTrigger className="h-10" data-testid="filter-category">
                <Filter size={14} className="mr-2 text-stone-400" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Categories</SelectItem>
                {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="relative flex-1 min-w-[160px]">
            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <Input value={filterCity} onChange={e => setFilterCity(e.target.value)} placeholder="Filter by city or ZIP" className="pl-9 h-10" data-testid="filter-city" />
          </div>
        </div>

        {/* Posts Feed */}
        {loading ? (
          <div className="text-center py-16"><Loader2 size={28} className="animate-spin mx-auto text-stone-400" /></div>
        ) : posts.length === 0 ? (
          <Card className="p-12 text-center">
            <MessageCircle size={48} className="mx-auto mb-4 text-stone-300" />
            <p className="text-lg text-stone-600">No posts yet</p>
            <p className="text-sm text-stone-400 mt-1">{isLoggedIn ? 'Be the first to share something!' : 'Log in to create a post.'}</p>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="posts-feed">
            {posts.map(post => (
              <Card key={post.id} className="bg-white rounded-xl overflow-hidden card-shadow" data-testid={`post-${post.id}`}>
                <div className="p-5">
                  {/* Author row */}
                  <div className="flex items-center gap-3 mb-3">
                    {post.author_photo ? (
                      <img src={post.author_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-[#1A4D2E] flex items-center justify-center text-white font-bold text-sm">
                        {(post.author_name || '?')[0]}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-stone-900 text-sm">{post.author_name}</p>
                      <div className="flex items-center gap-2 text-xs text-stone-500">
                        <span className="capitalize">{post.author_role}</span>
                        <span>&middot;</span>
                        <span>{formatDate(post.created_at)}</span>
                        {post.city && <><span>&middot;</span><span className="flex items-center gap-0.5"><MapPin size={10} />{post.city}</span></>}
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      post.visibility === 'public' ? 'bg-green-100 text-green-700' :
                      post.visibility === 'agents_only' ? 'bg-blue-100 text-blue-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>{visLabel(post.visibility)}</span>
                  </div>

                  {/* Category badge + Title */}
                  <span className="inline-block px-2 py-0.5 bg-[#1A4D2E]/10 text-[#1A4D2E] text-xs font-medium rounded-full mb-2">{post.category}</span>
                  <h2 className="text-lg font-bold text-stone-900 mb-2">{post.title}</h2>
                  <p className="text-sm text-stone-700 whitespace-pre-line leading-relaxed">{post.description}</p>

                  {/* Attachment */}
                  {post.attachment_url && (
                    <a href={post.attachment_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-stone-50 border border-stone-200 rounded-lg text-sm text-[#1A4D2E] hover:bg-stone-100 transition-colors">
                      <Paperclip size={14} />{post.attachment_name || 'Attachment'}
                    </a>
                  )}

                  {/* Actions bar */}
                  <div className="flex items-center gap-1 mt-4 pt-3 border-t border-stone-100">
                    <button onClick={() => toggleLike(post.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${post.is_liked ? 'text-red-500 bg-red-50' : 'text-stone-500 hover:bg-stone-50'}`} data-testid={`like-${post.id}`}>
                      <Heart size={16} fill={post.is_liked ? 'currentColor' : 'none'} />{post.likes_count || 0}
                    </button>
                    <button onClick={() => toggleComments(post.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${expandedComments[post.id] ? 'text-[#1A4D2E] bg-[#1A4D2E]/10' : 'text-stone-500 hover:bg-stone-50'}`} data-testid={`comments-${post.id}`}>
                      <MessageCircle size={16} />{post.comments_count || 0}
                    </button>
                    <button onClick={() => toggleSave(post.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition-colors ${post.is_saved ? 'text-amber-500 bg-amber-50' : 'text-stone-500 hover:bg-stone-50'}`} data-testid={`save-${post.id}`}>
                      <Bookmark size={16} fill={post.is_saved ? 'currentColor' : 'none'} />{post.saves_count || 0}
                    </button>
                    <button onClick={() => handleShare(post)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-stone-500 hover:bg-stone-50 transition-colors" data-testid={`share-${post.id}`}>
                      <Share2 size={16} />
                    </button>
                    <div className="flex-1" />
                    {(!agent || post.author_id !== agent.id) && (
                      <button onClick={() => handleContact(post.id)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-[#1A4D2E] bg-[#1A4D2E]/10 hover:bg-[#1A4D2E]/20 font-medium transition-colors" data-testid={`contact-${post.id}`}>
                        <Phone size={14} /> Contact
                      </button>
                    )}
                    {agent && post.author_id === agent.id && (
                      <button onClick={() => openEditPost(post)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm text-blue-600 bg-blue-50 hover:bg-blue-100 font-medium transition-colors" data-testid={`edit-post-${post.id}`}>
                        <FileText size={14} /> Edit
                      </button>
                    )}
                    {isLoggedIn && (
                      <button onClick={() => { setShowReport(post.id); setReportReason(''); }} className="flex items-center gap-1 px-2 py-1.5 rounded-full text-xs text-stone-400 hover:text-red-500 hover:bg-red-50 transition-colors" data-testid={`report-${post.id}`}>
                        <Flag size={14} />
                      </button>
                    )}
                  </div>

                  {/* Comments section */}
                  {expandedComments[post.id] && (
                    <div className="mt-3 pt-3 border-t border-stone-100">
                      <div className="flex gap-2 mb-3">
                        {!isLoggedIn && (
                          <Input value={commentNames[post.id] || ''} onChange={e => setCommentNames(prev => ({ ...prev, [post.id]: e.target.value }))}
                            placeholder="Name (optional)" className="text-sm h-9 w-32 flex-shrink-0" />
                        )}
                        <Input value={commentTexts[post.id] || ''} onChange={e => setCommentTexts(prev => ({ ...prev, [post.id]: e.target.value }))}
                          placeholder="Write a comment..." className="text-sm h-9" onKeyDown={e => e.key === 'Enter' && submitComment(post.id)} />
                        <Button size="sm" className="bg-[#1A4D2E] h-9 px-3" onClick={() => submitComment(post.id)}><Send size={14} /></Button>
                      </div>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {(postComments[post.id] || []).map(c => (
                          <div key={c.id} className="flex gap-2 p-2 bg-stone-50 rounded-lg">
                            {c.user_photo ? (
                              <img src={c.user_photo} alt="" className="w-7 h-7 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-7 h-7 rounded-full bg-stone-300 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{(c.user_name || '?')[0]}</div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-xs"><span className="font-semibold text-stone-800">{c.user_name}</span> <span className="text-stone-400">&middot; {formatDate(c.created_at)}</span></p>
                              <p className="text-sm text-stone-700 mt-0.5">{c.text}</p>
                            </div>
                            {agent && c.user_id === agent.id && (
                              <button onClick={() => deleteComment(post.id, c.id)} className="text-stone-300 hover:text-red-500 flex-shrink-0"><Trash2 size={12} /></button>
                            )}
                          </div>
                        ))}
                        {(postComments[post.id] || []).length === 0 && <p className="text-xs text-stone-400 text-center py-2">No comments yet</p>}
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            ))}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-6">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="rounded-full"><ChevronLeft size={16} /></Button>
                <span className="text-sm text-stone-600 px-2">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="rounded-full"><ChevronRight size={16} /></Button>
              </div>
            )}
          </div>
        )}

        {/* Create Post Dialog */}
        <Dialog open={showCreate} onOpenChange={(open) => { if (!open) { setShowCreate(false); setEditingPostId(null); setForm({ title: '', category: '', city: '', zip_code: '', description: '', visibility: 'public', attachment_url: '', attachment_name: '' }); } }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader><DialogTitle className="text-xl font-bold text-[#1A4D2E]">{editingPostId ? 'Edit Post' : 'Create Post'}</DialogTitle></DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label>Title</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Post title" data-testid="post-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Category</Label>
                  <Select value={form.category || '__none__'} onValueChange={v => setForm({ ...form, category: v === '__none__' ? '' : v })}>
                    <SelectTrigger data-testid="post-category"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__" disabled>Select category</SelectItem>
                      {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Visibility</Label>
                  <Select value={form.visibility} onValueChange={v => setForm({ ...form, visibility: v })}>
                    <SelectTrigger data-testid="post-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {visibilityOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} placeholder="e.g. Dayton" /></div>
                <div><Label>ZIP Code</Label><Input value={form.zip_code} onChange={e => setForm({ ...form, zip_code: e.target.value })} placeholder="e.g. 08810" /></div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={4} placeholder="What would you like to share?" data-testid="post-description" />
              </div>
              <div>
                <Label>Attachment URL (optional)</Label>
                <Input value={form.attachment_url} onChange={e => setForm({ ...form, attachment_url: e.target.value })} placeholder="https://..." />
                {form.attachment_url && <Input value={form.attachment_name} onChange={e => setForm({ ...form, attachment_name: e.target.value })} placeholder="File name (optional)" className="mt-2" />}
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                <Button onClick={handleCreate} disabled={creating} className="bg-[#1A4D2E] hover:bg-[#143D24] text-white" data-testid="submit-post">
                  {creating ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />} {editingPostId ? 'Update' : 'Post'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Contact Dialog */}
        <Dialog open={!!showContact} onOpenChange={() => setShowContact(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle>Contact Information</DialogTitle></DialogHeader>
            {contactInfo && (
              <div className="space-y-3 pt-2">
                <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg"><User size={18} className="text-stone-500" /><span className="text-sm font-medium">{contactInfo.name}</span></div>
                {contactInfo.email && (
                  <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                    <Mail size={18} className="text-[#1A4D2E]" /><span className="text-sm">{contactInfo.email}</span>
                  </a>
                )}
                {contactInfo.phone && (
                  <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                    <Phone size={18} className="text-[#1A4D2E]" /><span className="text-sm">{contactInfo.phone}</span>
                  </a>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Report Dialog */}
        <Dialog open={!!showReport} onOpenChange={() => setShowReport(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader><DialogTitle className="text-red-600">Report Post</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <Textarea value={reportReason} onChange={e => setReportReason(e.target.value)} placeholder="Why is this post inappropriate?" rows={3} />
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowReport(null)}>Cancel</Button>
                <Button onClick={handleReport} className="bg-red-600 hover:bg-red-700 text-white" data-testid="submit-report"><Flag size={14} className="mr-1.5" />Report</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Wrapper>
  );
};

export default AlbriksConnect;
